// src/modules/restaurant/pages/respayment/components/paymentSection/OrderSummaryCard.tsx
import React, { useState, useEffect } from "react";
import { Divider } from "@mui/material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { Order } from "src/shared/services/interfacev2";
import { useFormatAmount } from "src/business-types/laundry/modules/home/hooks/useFormatAmount";
import { useUser } from "src/shared/context/UserContext";
import CommonButtons from "src/shared/components/CommonButtons";

interface Props {
  order: Order | null;
  tip: number;
  total: number;
  themeTextColor: string;
  getLightBgClass: () => string;
  onConfirmEdit: (data: { parcelFee: number; deliveryFee: number; discount: number }) => void;
  setHasUnsavedChanges: (value: boolean) => void;
  onFeeChange?: (fees: { parcelFee: number; deliveryFee: number; discount: number }) => void; // NEW: callback for real-time updates
}

const OrderSummaryCard: React.FC<Props> = ({
  order,
  tip,
  total,
  themeTextColor,
  getLightBgClass,
  onConfirmEdit,
  setHasUnsavedChanges,
  onFeeChange, // NEW
}) => {
  const formatAmount = useFormatAmount();
  const { userConfigration } = useUser();

  const txn = order?.orders?.[0];

  const toBoolean = (value: any) => value === true || value === "true";

  const deliveryFeeEnabled = toBoolean(userConfigration?.deliveryFeeEnabled);
  const parcelFeeEnabled = toBoolean(userConfigration?.parcelFeeEnabled);
  const isDeliveryOrder = order?.orderDetails?.[0]?.orderType?.toLowerCase() === "delivery";

  const [parcelFee, setParcelFee] = useState<string>("0");
  const [deliveryFee, setDeliveryFee] = useState<string>("0");
  const [discount, setDiscount] = useState<string>("0");
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isParcelFocused, setIsParcelFocused] = useState(false);
  const [isDeliveryFocused, setIsDeliveryFocused] = useState(false);
  const [isDiscountFocused, setIsDiscountFocused] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const [initialValues, setInitialValues] = useState({
    parcelFee: 0,
    deliveryFee: 0,
    discount: 0,
  });

  // Allow only decimal values
  const handleDecimalChange = (value: string, setter: React.Dispatch<React.SetStateAction<string>>) => {
    if (/^\d*\.?\d*$/.test(value)) {
      setter(value);
    }
  };

  // Sync API values
  useEffect(() => {
    if (!txn || hasInitialized) return;

    const initial = {
      parcelFee: Number(txn?.parcelFee ?? 0),
      deliveryFee: Number(txn?.deliveryFee ?? 0),
      discount: Number(txn?.discount ?? 0),
    };

    setParcelFee(String(initial.parcelFee));
    setDeliveryFee(String(initial.deliveryFee));
    setDiscount(String(initial.discount));
    setInitialValues(initial);
    setHasInitialized(true);
  }, [txn, hasInitialized]);

  // Send real-time fee updates to parent whenever fees change
  useEffect(() => {
    if (onFeeChange && hasInitialized) {
      const currentFees = {
        parcelFee: Number(parcelFee) || 0,
        deliveryFee: Number(deliveryFee) || 0,
        discount: Number(discount) || 0,
      };
      onFeeChange(currentFees);
    }
  }, [parcelFee, deliveryFee, discount, hasInitialized, onFeeChange]);

  const isChanged = Number(parcelFee) !== initialValues.parcelFee || Number(deliveryFee) !== initialValues.deliveryFee || Number(discount) !== initialValues.discount;

  useEffect(() => {
    setHasUnsavedChanges(isChanged);
  }, [isChanged, setHasUnsavedChanges]);

  const handleConfirm = async () => {
    setIsConfirming(true);

    const data = {
      parcelFee: Number(parcelFee) || 0,
      deliveryFee: Number(deliveryFee) || 0,
      discount: Number(discount) || 0,
    };

    try {
      await onConfirmEdit(data);
      setInitialValues(data);
      setHasUnsavedChanges(false);
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div className="mb-2">
      <div className="flex items-center gap-2 -mb-2 -mt-2.5">
        <div className={`p-1 ${getLightBgClass()} rounded-lg`}>
          <ReceiptIcon className={`${themeTextColor} text-lg`} />
        </div>

        <h3 className="font-bold py-2 text-gray-800 text-lg">Order Summary</h3>
      </div>

      <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-2 border border-gray-200 space-y-2">
        {/* Subtotal */}
        <div className="flex justify-between">
          <span>Subtotal</span>
          <span>{formatAmount(Number(txn?.orderAmount) || 0)}</span>
        </div>

        {/* Parcel Fee */}
        {parcelFeeEnabled && (
          <div className="flex justify-between items-center">
            <span>Parcel Fee</span>
            <input
              type="text"
              inputMode="decimal"
              placeholder="0.00"
              className="w-20 border rounded px-1 text-right"
              value={parcelFee === "0" ? "" : isParcelFocused ? parcelFee : formatAmount(Number(parcelFee))}
              onFocus={() => setIsParcelFocused(true)}
              onBlur={() => setIsParcelFocused(false)}
              onChange={(e) => handleDecimalChange(e.target.value, setParcelFee)}
            />
          </div>
        )}

        {/* Delivery Fee */}
        {deliveryFeeEnabled && (
          <div className="flex justify-between items-center">
            <span>Delivery Fee</span>
            {deliveryFeeEnabled && isDeliveryOrder ? (
              <input
                type="text"
                inputMode="decimal"
                placeholder="0.00"
                className="w-20 border rounded px-1 text-right"
                value={deliveryFee === "0" ? "" : isDeliveryFocused ? deliveryFee : formatAmount(Number(deliveryFee))}
                onFocus={() => setIsDeliveryFocused(true)}
                onBlur={() => setIsDeliveryFocused(false)}
                onChange={(e) => handleDecimalChange(e.target.value, setDeliveryFee)}
              />
            ) : (
              <span>{formatAmount(0)}</span>
            )}
          </div>
        )}

        {/* Discount */}
        <div className="flex justify-between items-center">
          <span>Discount</span>
          <input
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            className="w-20 border rounded px-1 text-right"
            value={discount === "0" ? "" : isDiscountFocused ? discount : formatAmount(Number(discount))}
            onFocus={() => setIsDiscountFocused(true)}
            onBlur={() => setIsDiscountFocused(false)}
            onChange={(e) => handleDecimalChange(e.target.value, setDiscount)}
          />
        </div>

        {/* Tip */}
        <div className="flex justify-between">
          <span>Tip</span>
          <span>{formatAmount(tip)}</span>
        </div>

        <Divider />

        {/* Total - This will now update in real-time */}
        <div className="flex justify-between font-bold">
          <span>TOTAL</span>
          <span>{formatAmount(total)}</span>
        </div>

        <CommonButtons label={isConfirming ? "Confirming..." : "Confirm Changes"} className="mt-2 w-full" onClick={handleConfirm} disabled={!isChanged || isConfirming} />
      </div>
    </div>
  );
};

export default OrderSummaryCard;
